// src/pages/services/AfterPillPage.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPills, FaInfoCircle, FaArrowRight, FaClock, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface PillChildProps { ipAllowed: boolean }

type NoticeDoc = { title?: string; html?: string };

const NOTICE_VERSION = 'v2025-10-01';

function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const AfterPillPage: React.FC<PillChildProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);

  // 薬品価格（DB優先、フォールバックは静的データ）
  const { medicines } = useMedicinePrices('pill', lang);

  const [openNotice, setOpenNotice] = useState(false);
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const openNoticeModal = async () => {
    setAgree(false);
    setOpenNotice(true);
    try {
      setDocLoading(true);
      const base = getPublicBase();
      const primary = `${base}/data/notices/pill_after.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/pill_after.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(
        NOTICE_VERSION
      )}`;
      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };
      let json = await tryFetch(primary);
      if (!json) json = await tryFetch(fallback);
      setDoc(json || { title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenNotice(false); };
    if (openNotice) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [openNotice]);

  const proceedReserve = () => {
    if (!agree) return;
    setOpenNotice(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'pill',
      preAgreed: true,
      from: 'service',
      pillNoticeScope: 'pill_after',
    });
  };

  const L = {
    ja: {
      pageTitle: 'アフターピル・避妊',
      helmetTitle: 'アフターピル・避妊 - Melodis Clinic',
      back: '戻る',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療'],
      overview: '概要',
      lead1:
        '避妊ができなかった場合でも、できるだけ早く（24時間以内推奨）内服することで高い妊娠回避率が期待できます。120時間以内でも一定の効果が見込めます。',
      lead2:
        'ホルモン剤の内服により受精卵の着床を妨げる働きがあります。副作用として、嘔気・頭痛・倦怠感などが数％の確率で出現することがありますが、多くは24時間以内におさまります。',
      timingTitle: '服用のタイミング',
      timingText:
        '早いほど有効性が高まります。受診時に最終月経/性交渉の時刻・既往歴・内服薬を確認します。',
      precautionTitle: '注意事項',
      precautionBullets: [
        '次回以降の避妊は確保してください（低用量ピルの併用提案など）。',
        '嘔吐時は再投与の可否を医師に相談してください。',
        '持病・併用薬がある方は必ず申告してください。',
      ],
      priceTitle: '価格（診察料 0円）：',
      priceItems: [
        { name: 'アフターピル (Emergency Contraceptive)', dosage: '1回分', price: '8,000', taxPrice: '8,800' },
      ],
      noticeBtn: '注意事項はこちらから',
      backToList: 'ピル一覧へ戻る',
      reserveCta: 'オンライン診療を予約する',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      pageTitle: '紧急避孕药・避孕',
      helmetTitle: '紧急避孕药・避孕 - Melodis Clinic',
      back: '返回',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗'],
      overview: '概览',
      lead1:
        '在未能成功避孕的情况下，若能尽快服用（建议 24 小时内），可显著提高避孕成功率；即使在 120 小时内也仍可能有效。',
      lead2:
        '药物通过激素作用干预受精卵着床。可能出现恶心、头痛、乏力等不适，通常在 24 小时内缓解。',
      timingTitle: '服用时机',
      timingText:
        '越早服用越有效。就诊时会确认末次月经/性行为时间、既往史及在用药物。',
      precautionTitle: '注意事项',
      precautionBullets: [
        '请为后续性行为做好常规避孕（可考虑低剂量口服避孕药等）。',
        '若发生呕吐，请尽快咨询医生评估是否需要补服。',
        '如有慢性病或正在服用其他药物，请务必告知。',
      ],
      priceTitle: '价格（诊察费 0 日元）：',
      priceItems: [
        { name: '紧急避孕药 (Emergency Contraceptive)', dosage: '1次份', price: '8,000', taxPrice: '8,800' },
      ],
      noticeBtn: '查看注意事项',
      backToList: '返回避孕药一览',
      reserveCta: '立即预约在线咨询',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  // 価格表は DB(admin設定)優先。'pill' サービスの薬品から本ページの薬(アフターピル)だけ名称で抽出。
  // price/taxPrice は本ページJSXが文字列前提のため 'ja-JP' 整形。空ならハードコード L[lang].priceItems にフォールバック。
  const dbPriceItems = toSinglePriceItems(medicines, lang)
    .filter((it) => /Emergency|Contracept|アフターピル|紧急避孕|レボノルゲストレル|ノルレボ|Levonorgestrel|NorLevo/i.test(it.name))
    .map((it) => ({ ...it, price: it.price.toLocaleString('ja-JP'), taxPrice: it.taxPrice.toLocaleString('ja-JP') }));
  const priceItems: any[] = dbPriceItems.length > 0 ? dbPriceItems : [...L[lang].priceItems];

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{L[lang].helmetTitle}</title>
        <meta name="description" content={t('seo.after_pill.description')} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">

          {/* 上部：戻る / 返回 */}
          <div className="mb-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].back}
            </button>
          </div>

          {/* 見出し / 标题 */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-sky-100 p-3">
              <FaPills className="text-sky-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-sky-400 mr-2">│</span>{L[lang].pageTitle}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.after_pill.summary')}
          </p>

          {/* 概要 */}
          <section className="rounded-2xl border border-sky-100 bg-[#fff9f8] p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-sky-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-sky-700">{L[lang].overview}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li key={i} className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-slate-800 text-center shadow-sm font-medium">
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed">
              <p>{L[lang].lead1}</p>
              <p>{L[lang].lead2}</p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    <FaClock className="inline-block mr-2" /> {L[lang].timingTitle}
                  </h3>
                  <p className="text-slate-700">{L[lang].timingText}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{L[lang].precautionTitle}</h3>
                  <ul className="list-disc pl-5 space-y-2 text-slate-700">
                    {L[lang].precautionBullets.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              </div>

              {/* 価格 */}
              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-5 md:p-8">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
                
                <ul className="space-y-4 text-slate-700">
                  {priceItems.map((item: any, i: number) => (
                    <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1 border-b border-slate-50 last:border-0 pb-3">
                      <span className="flex flex-col">
                        <span className="font-bold text-slate-800">{item.name}</span>
                        <span className="text-[11px] text-slate-500">{item.dosage}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-lg text-slate-900">{item.price}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={openNoticeModal}
                    className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-6 py-2.5 text-sm font-medium text-sky-700 hover:bg-sky-100 transition-colors shadow-sm"
                  >
                    {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA（→ モーダル経由） */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/pill')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].backToList}
            </button>
            {isAuthenticated && (
              <button
                onClick={openNoticeModal}
                className="inline-block bg-sky-600 hover:bg-sky-600 active:bg-sky-700 text-white px-8 py-3 rounded-full font-semibold shadow-md transition-all hover:-translate-y-0.5"
              >
                {L[lang].reserveCta}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 注意事項モーダル（pill_after） */}
      {openNotice && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setOpenNotice(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setOpenNotice(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-sky-600 hover:bg-sky-600 active:bg-sky-700' : 'bg-sky-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setOpenNotice(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AfterPillPage;
